import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Package,
  Edit,
  Trash2,
  RefreshCw,
  Download,
  Upload,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  Building2,
  DollarSign,
  Key,
  Mail,
  Phone
} from 'lucide-react';
import { apiService, Device } from '../services/api';
import DevicePicker from './DevicePicker';
import { HubPage, HubPanel, HubTabs } from './ui/HubShell';

interface License {
  license_id: string;
  software_name: string;
  software_version?: string;
  vendor?: string;
  license_type: string;
  total_licenses: number;
  available_licenses: number;
  assigned_count?: number;
  purchase_date?: string;
  expiry_date?: string;
  renewal_date?: string;
  purchase_cost?: number;
  currency: string;
  purchase_order_number?: string;
  vendor_contact_email?: string;
  vendor_contact_phone?: string;
  license_key?: string;
  notes?: string;
  status: string;
  auto_renew: boolean;
  renewal_reminder_days: number;
  expiry_status?: string;
  days_until_expiry?: number;
  created_at: string;
  updated_at: string;
}

interface LicenseAssignment {
  assignment_id: string;
  license_id: string;
  device_id?: string;
  user_id?: string;
  user_name?: string;
  user_email?: string;
  assigned_date: string;
  unassigned_date?: string;
  status: string;
  assigned_by?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface LicenseRenewal {
  renewal_id: string;
  license_id: string;
  renewal_date: string;
  expiry_date: string;
  renewal_cost?: number;
  currency: string;
  renewal_type?: string;
  purchase_order_number?: string;
  notes?: string;
  renewed_by?: string;
  created_at: string;
}

const LicenseManagement: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'licenses' | 'assignments' | 'renewals' | 'expiring'>('licenses');
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVendor, setFilterVendor] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterExpiryStatus, setFilterExpiryStatus] = useState<string>('');
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [assignments, setAssignments] = useState<LicenseAssignment[]>([]);
  const [renewals, setRenewals] = useState<LicenseRenewal[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [expiringLicenses, setExpiringLicenses] = useState<License[]>([]);

  // Form state for new/edit license
  const [licenseForm, setLicenseForm] = useState({
    software_name: '',
    software_version: '',
    vendor: '',
    license_type: 'subscription',
    total_licenses: 1,
    purchase_date: '',
    expiry_date: '',
    renewal_date: '',
    purchase_cost: '',
    currency: 'USD',
    purchase_order_number: '',
    vendor_contact_email: '',
    vendor_contact_phone: '',
    license_key: '',
    notes: '',
    status: 'active',
    auto_renew: false,
    renewal_reminder_days: 30
  });

  // Form state for assignment
  const [assignmentForm, setAssignmentForm] = useState({
    device_id: '',
    user_id: '',
    user_name: '',
    user_email: '',
    notes: ''
  });

  // Form state for renewal
  const [renewalForm, setRenewalForm] = useState({
    renewal_date: '',
    expiry_date: '',
    renewal_cost: '',
    currency: 'USD',
    renewal_type: 'annual',
    purchase_order_number: '',
    notes: ''
  });

  useEffect(() => {
    loadLicenses();
    loadDevices();
    loadExpiringLicenses();
  }, []);

  useEffect(() => {
    if (selectedLicense) {
      loadAssignments();
      loadRenewals();
    }
  }, [selectedLicense]);

  // Auto-reload licenses when filters change
  useEffect(() => {
    // Debounce search term to avoid too many API calls while typing
    // For dropdowns, reload immediately
    const timeoutId = setTimeout(() => {
      loadLicenses();
    }, searchTerm ? 500 : 0); // 500ms delay for search, immediate for dropdowns

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterVendor, filterStatus, filterExpiryStatus]);

  const loadLicenses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getLicenses({
        skip: 0,
        limit: 1000,
        search: searchTerm || undefined,
        vendor: filterVendor || undefined,
        status: filterStatus || undefined,
        expiry_status: filterExpiryStatus || undefined
      });
      
      // Remove duplicates based on license_id
      const licensesMap = new Map();
      (response.items || []).forEach((license: License) => {
        if (!licensesMap.has(license.license_id)) {
          licensesMap.set(license.license_id, license);
        }
      });
      
      // Convert map back to array and sort by software name
      const uniqueLicenses = Array.from(licensesMap.values()).sort((a, b) => {
        const nameA = (a.software_name || '').toLowerCase();
        const nameB = (b.software_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setLicenses(uniqueLicenses);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load licenses');
      console.error('Error loading licenses:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      const devicesList = await apiService.getDevices();
      setDevices(devicesList);
    } catch (err) {
      console.error('Error loading devices:', err);
    }
  };

  const loadExpiringLicenses = async () => {
    try {
      const response = await apiService.getExpiringLicenses(90, { skip: 0, limit: 100 });
      
      // Remove duplicates based on license_id
      const licensesMap = new Map();
      (response.items || []).forEach((license: License) => {
        if (!licensesMap.has(license.license_id)) {
          licensesMap.set(license.license_id, license);
        }
      });
      
      // Convert map back to array
      const uniqueLicenses = Array.from(licensesMap.values());
      
      setExpiringLicenses(uniqueLicenses);
    } catch (err) {
      console.error('Error loading expiring licenses:', err);
    }
  };

  const loadAssignments = async () => {
    if (!selectedLicense) return;
    try {
      const response = await apiService.getLicenseAssignments(selectedLicense.license_id, {
        skip: 0,
        limit: 1000,
        status: 'active'
      });
      setAssignments(response.items || []);
    } catch (err) {
      console.error('Error loading assignments:', err);
    }
  };

  const loadRenewals = async () => {
    if (!selectedLicense) return;
    try {
      const response = await apiService.getLicenseRenewals(selectedLicense.license_id, {
        skip: 0,
        limit: 100
      });
      setRenewals(response.items || []);
    } catch (err) {
      console.error('Error loading renewals:', err);
    }
  };

  const handleCreateLicense = async () => {
    try {
      const licenseData = {
        ...licenseForm,
        total_licenses: parseInt(licenseForm.total_licenses.toString()),
        purchase_cost: licenseForm.purchase_cost ? parseFloat(licenseForm.purchase_cost.toString()) : null,
        purchase_date: licenseForm.purchase_date || null,
        expiry_date: licenseForm.expiry_date || null,
        renewal_date: licenseForm.renewal_date || null,
        renewal_reminder_days: parseInt(licenseForm.renewal_reminder_days.toString()),
        created_by: 'dashboard-user'
      };
      await apiService.createLicense(licenseData);
      setShowLicenseModal(false);
      resetLicenseForm();
      loadLicenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create license');
    }
  };

  const handleUpdateLicense = async () => {
    if (!selectedLicense) return;
    try {
      const licenseData = {
        ...licenseForm,
        total_licenses: parseInt(licenseForm.total_licenses.toString()),
        purchase_cost: licenseForm.purchase_cost ? parseFloat(licenseForm.purchase_cost.toString()) : null,
        purchase_date: licenseForm.purchase_date || null,
        expiry_date: licenseForm.expiry_date || null,
        renewal_date: licenseForm.renewal_date || null,
        renewal_reminder_days: parseInt(licenseForm.renewal_reminder_days.toString())
      };
      await apiService.updateLicense(selectedLicense.license_id, licenseData);
      setShowLicenseModal(false);
      setSelectedLicense(null);
      resetLicenseForm();
      loadLicenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update license');
    }
  };

  const handleDeleteLicense = async (licenseId: string, softwareName: string) => {
    if (!window.confirm(`Are you sure you want to delete license for "${softwareName}"? This will also delete all assignments and renewal history.`)) {
      return;
    }
    try {
      await apiService.deleteLicense(licenseId);
      loadLicenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete license');
    }
  };

  const handleAssignLicense = async () => {
    if (!selectedLicense) return;
    try {
      await apiService.assignLicense(selectedLicense.license_id, {
        ...assignmentForm,
        device_id: assignmentForm.device_id || null,
        assigned_by: 'dashboard-user'
      });
      setShowAssignModal(false);
      resetAssignmentForm();
      loadAssignments();
      loadLicenses(); // Refresh to update available_licenses
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign license');
    }
  };

  const handleReleaseAssignment = async (assignmentId: string) => {
    if (!window.confirm('Are you sure you want to release this license assignment?')) {
      return;
    }
    try {
      await apiService.releaseLicenseAssignment(assignmentId);
      loadAssignments();
      loadLicenses(); // Refresh to update available_licenses
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to release assignment');
    }
  };

  const handleRenewLicense = async () => {
    if (!selectedLicense) return;
    try {
      await apiService.renewLicense(selectedLicense.license_id, {
        ...renewalForm,
        renewal_date: renewalForm.renewal_date,
        expiry_date: renewalForm.expiry_date,
        renewal_cost: renewalForm.renewal_cost ? parseFloat(renewalForm.renewal_cost.toString()) : null,
        renewed_by: 'dashboard-user'
      });
      setShowRenewalModal(false);
      resetRenewalForm();
      loadRenewals();
      loadLicenses(); // Refresh to update expiry dates
      loadExpiringLicenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to renew license');
    }
  };

  const resetLicenseForm = () => {
    setLicenseForm({
      software_name: '',
      software_version: '',
      vendor: '',
      license_type: 'subscription',
      total_licenses: 1,
      purchase_date: '',
      expiry_date: '',
      renewal_date: '',
      purchase_cost: '',
      currency: 'USD',
      purchase_order_number: '',
      vendor_contact_email: '',
      vendor_contact_phone: '',
      license_key: '',
      notes: '',
      status: 'active',
      auto_renew: false,
      renewal_reminder_days: 30
    });
  };

  const resetAssignmentForm = () => {
    setAssignmentForm({
      device_id: '',
      user_id: '',
      user_name: '',
      user_email: '',
      notes: ''
    });
  };

  const resetRenewalForm = () => {
    setRenewalForm({
      renewal_date: '',
      expiry_date: '',
      renewal_cost: '',
      currency: 'USD',
      renewal_type: 'annual',
      purchase_order_number: '',
      notes: ''
    });
  };

  const openEditModal = (license: License) => {
    setSelectedLicense(license);
    setLicenseForm({
      software_name: license.software_name,
      software_version: license.software_version || '',
      vendor: license.vendor || '',
      license_type: license.license_type,
      total_licenses: license.total_licenses,
      purchase_date: license.purchase_date ? license.purchase_date.split('T')[0] : '',
      expiry_date: license.expiry_date ? license.expiry_date.split('T')[0] : '',
      renewal_date: license.renewal_date ? license.renewal_date.split('T')[0] : '',
      purchase_cost: license.purchase_cost?.toString() || '',
      currency: license.currency,
      purchase_order_number: license.purchase_order_number || '',
      vendor_contact_email: license.vendor_contact_email || '',
      vendor_contact_phone: license.vendor_contact_phone || '',
      license_key: license.license_key || '',
      notes: license.notes || '',
      status: license.status,
      auto_renew: license.auto_renew,
      renewal_reminder_days: license.renewal_reminder_days
    });
    setShowLicenseModal(true);
  };

  const openAssignModal = (license: License) => {
    setSelectedLicense(license);
    resetAssignmentForm();
    setShowAssignModal(true);
  };

  const openRenewalModal = (license: License) => {
    setSelectedLicense(license);
    setRenewalForm({
      renewal_date: new Date().toISOString().split('T')[0],
      expiry_date: license.expiry_date ? new Date(new Date(license.expiry_date).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : '',
      renewal_cost: '',
      currency: license.currency,
      renewal_type: 'annual',
      purchase_order_number: '',
      notes: ''
    });
    setShowRenewalModal(true);
  };

  const getExpiryStatusBadge = (expiryStatus?: string, daysUntilExpiry?: number) => {
    if (!expiryStatus) return null;
    
    switch (expiryStatus) {
      case 'expired':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">Expired</span>;
      case 'expiring_soon':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">Expiring Soon ({daysUntilExpiry} days)</span>;
      case 'expiring_quarter':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Expiring in {daysUntilExpiry} days</span>;
      case 'active':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Active</span>;
      case 'no_expiry':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">No Expiry</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">{expiryStatus}</span>;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount?: number, currency: string = 'USD') => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const handleBulkImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !['xlsx', 'xls', 'csv'].includes(fileExtension)) {
      setError('Please select an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await apiService.bulkImportLicenses(file);
      const successMsg = `Successfully imported ${result.success_count || result.imported?.length || 0} licenses.`;
      const errorMsg = result.errors?.length ? `\n${result.error_count || result.errors.length} errors occurred.` : '';
      alert(successMsg + errorMsg);
      if (result.errors && result.errors.length > 0) {
        console.error('Import errors:', result.errors);
        // Show first few errors in console or could display in UI
        const errorPreview = result.errors.slice(0, 5).join('\n');
        if (result.errors.length > 5) {
          console.warn(`... and ${result.errors.length - 5} more errors`);
        }
      }
      loadLicenses();
      loadExpiringLicenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import licenses');
    } finally {
      setLoading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleExportLicenses = async (format: 'csv' | 'xlsx' = 'xlsx') => {
    setLoading(true);
    setError(null);
    try {
      const blob = await apiService.exportLicenses(format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extension = format === 'xlsx' ? 'xlsx' : 'csv';
      a.download = `licenses_export_${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export licenses');
    } finally {
      setLoading(false);
    }
  };

  return (
    <HubPage
      title={t('licenseManagement.title')}
      subtitle={t('licenseManagement.description')}
      icon={<Key size={20} />}
      eyebrow="Assets"
      action={
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => handleExportLicenses('xlsx')}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors"
            title="Export to Excel (.xlsx)"
          >
            <Download size={18} />
            {t('licenseManagement.exportExcel')}
          </button>
          <label className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 transition-colors cursor-pointer">
            <Upload size={18} />
            {t('licenseManagement.bulkImport')}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleBulkImport}
              className="hidden"
            />
          </label>
          <button
            type="button"
            onClick={() => {
              setSelectedLicense(null);
              resetLicenseForm();
              setShowLicenseModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} />
            {t('licenseManagement.addLicense')}
          </button>
        </div>
      }
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      <HubPanel withTabs padded={false}>
        <HubTabs
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { id: 'licenses', label: `${t('licenseManagement.licenses')} (${licenses.length})`, icon: <Package size={15} /> },
            { id: 'expiring', label: `${t('licenseManagement.expiring')} (${expiringLicenses.length})`, icon: <AlertCircle size={15} /> },
            { id: 'assignments', label: 'Assignments', icon: <Users size={15} /> },
            { id: 'renewals', label: 'Renewals', icon: <RefreshCw size={15} /> },
          ]}
        />
        <div className="hub-panel__inner">
      {activeTab === 'licenses' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search licenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadLicenses()}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={filterVendor}
              onChange={(e) => setFilterVendor(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Vendors</option>
              {Array.from(new Set(licenses.map(l => l.vendor).filter(Boolean))).map(vendor => (
                <option key={vendor} value={vendor}>{vendor}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
              <option value="suspended">Suspended</option>
            </select>
            <select
              value={filterExpiryStatus}
              onChange={(e) => setFilterExpiryStatus(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Expiry Status</option>
              <option value="expired">Expired</option>
              <option value="expiring_soon">Expiring Soon</option>
              <option value="expiring_quarter">Expiring This Quarter</option>
              <option value="active">Active</option>
              <option value="no_expiry">No Expiry</option>
            </select>
            <button
              onClick={loadLicenses}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-2"
            >
              <RefreshCw size={18} />
              Refresh
            </button>
          </div>

          {/* Licenses Table */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Software</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Licenses</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Purchase Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Expiry Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">Loading licenses...</td>
                  </tr>
                ) : licenses.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">No licenses found</td>
                  </tr>
                ) : (
                  licenses.map((license) => (
                    <tr key={license.license_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{license.software_name}</div>
                        {license.software_version && (
                          <div className="text-sm text-slate-500">v{license.software_version}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{license.vendor || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                          {license.license_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <span className="font-medium">{license.assigned_count || 0}</span>
                          <span className="text-slate-500"> / {license.total_licenses}</span>
                        </div>
                        <div className="text-xs text-slate-500">{license.available_licenses} available</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{formatDate(license.purchase_date)}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-600">{formatDate(license.expiry_date)}</div>
                        {license.days_until_expiry !== undefined && license.days_until_expiry !== null && (
                          <div className="text-xs text-slate-500">
                            {license.days_until_expiry > 0 ? `${license.days_until_expiry} days left` : 'Expired'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getExpiryStatusBadge(license.expiry_status, license.days_until_expiry)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(license)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit License"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => openAssignModal(license)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                            title="Assign License"
                            disabled={license.available_licenses <= 0}
                          >
                            <Users size={16} />
                          </button>
                          <button
                            onClick={() => openRenewalModal(license)}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                            title="Renew License"
                          >
                            <RefreshCw size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteLicense(license.license_id, license.software_name)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete License"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Expiring Licenses Tab */}
      {activeTab === 'expiring' && (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-yellow-800">
              <AlertCircle size={20} />
              <span className="font-medium">Licenses Expiring Within 90 Days</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Software</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Expiry Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Days Until Expiry</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Auto Renew</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expiringLicenses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">No licenses expiring soon</td>
                  </tr>
                ) : (
                  expiringLicenses.map((license) => (
                    <tr key={license.license_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{license.software_name}</div>
                        {license.software_version && (
                          <div className="text-sm text-slate-500">v{license.software_version}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{license.vendor || 'N/A'}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDate(license.expiry_date)}</td>
                      <td className="px-4 py-3">
                        <span className={`font-medium ${
                          (license.days_until_expiry || 0) <= 30 ? 'text-red-600' :
                          (license.days_until_expiry || 0) <= 60 ? 'text-orange-600' : 'text-yellow-600'
                        }`}>
                          {license.days_until_expiry} days
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {license.auto_renew ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Yes</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openRenewalModal(license)}
                          className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                        >
                          Renew Now
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Assignments Tab */}
      {activeTab === 'assignments' && (
        <div className="space-y-4">
          {!selectedLicense ? (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center">
              <Users size={48} className="mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600">Select a license from the Licenses tab to view assignments</p>
            </div>
          ) : (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-blue-900">Assignments for: {selectedLicense.software_name}</h3>
                    <p className="text-sm text-blue-700">
                      {selectedLicense.assigned_count || 0} assigned, {selectedLicense.available_licenses} available
                    </p>
                  </div>
                  <button
                    onClick={() => openAssignModal(selectedLicense)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    disabled={selectedLicense.available_licenses <= 0}
                  >
                    <Plus size={18} className="inline mr-2" />
                    Assign License
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Device/User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Assigned Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Assigned By</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {assignments.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-slate-500">No assignments found</td>
                      </tr>
                    ) : (
                      assignments.map((assignment) => (
                        <tr key={assignment.assignment_id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            {assignment.device_id ? (
                              <div>
                                <div className="font-medium text-slate-900">
                                  {devices.find(d => d.device_id === assignment.device_id)?.name || assignment.device_id}
                                </div>
                                {assignment.user_name && (
                                  <div className="text-sm text-slate-500">{assignment.user_name}</div>
                                )}
                              </div>
                            ) : (
                              <div>
                                <div className="font-medium text-slate-900">{assignment.user_name || assignment.user_id}</div>
                                {assignment.user_email && (
                                  <div className="text-sm text-slate-500">{assignment.user_email}</div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{formatDate(assignment.assigned_date)}</td>
                          <td className="px-4 py-3 text-slate-600">{assignment.assigned_by || 'N/A'}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                              {assignment.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleReleaseAssignment(assignment.assignment_id)}
                              className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                            >
                              Release
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Renewals Tab */}
      {activeTab === 'renewals' && (
        <div className="space-y-4">
          {!selectedLicense ? (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center">
              <RefreshCw size={48} className="mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600">Select a license from the Licenses tab to view renewal history</p>
            </div>
          ) : (
            <>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-purple-900">Renewal History for: {selectedLicense.software_name}</h3>
                    <p className="text-sm text-purple-700">
                      Current expiry: {formatDate(selectedLicense.expiry_date)}
                    </p>
                  </div>
                  <button
                    onClick={() => openRenewalModal(selectedLicense)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Plus size={18} className="inline mr-2" />
                    Record Renewal
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Renewal Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Expiry Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Renewed By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {renewals.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-slate-500">No renewal history</td>
                      </tr>
                    ) : (
                      renewals.map((renewal) => (
                        <tr key={renewal.renewal_id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-600">{formatDate(renewal.renewal_date)}</td>
                          <td className="px-4 py-3 text-slate-600">{formatDate(renewal.expiry_date)}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                              {renewal.renewal_type || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {renewal.renewal_cost ? formatCurrency(renewal.renewal_cost, renewal.currency) : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{renewal.renewed_by || 'N/A'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

          </div>
        </HubPanel>

      {/* Add/Edit License Modal */}
      {showLicenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">
                {selectedLicense ? 'Edit License' : 'Add New License'}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Software Name *</label>
                  <input
                    type="text"
                    value={licenseForm.software_name}
                    onChange={(e) => setLicenseForm({ ...licenseForm, software_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., AutoCAD"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Version</label>
                  <input
                    type="text"
                    value={licenseForm.software_version}
                    onChange={(e) => setLicenseForm({ ...licenseForm, software_version: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 2024"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vendor</label>
                  <input
                    type="text"
                    value={licenseForm.vendor}
                    onChange={(e) => setLicenseForm({ ...licenseForm, vendor: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Autodesk"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">License Type *</label>
                  <select
                    value={licenseForm.license_type}
                    onChange={(e) => setLicenseForm({ ...licenseForm, license_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="perpetual">Perpetual</option>
                    <option value="subscription">Subscription</option>
                    <option value="trial">Trial</option>
                    <option value="volume">Volume</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Total Licenses *</label>
                  <input
                    type="number"
                    min="1"
                    value={licenseForm.total_licenses}
                    onChange={(e) => setLicenseForm({ ...licenseForm, total_licenses: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Purchase Date</label>
                  <input
                    type="date"
                    value={licenseForm.purchase_date}
                    onChange={(e) => setLicenseForm({ ...licenseForm, purchase_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Expiry Date</label>
                  <input
                    type="date"
                    value={licenseForm.expiry_date}
                    onChange={(e) => setLicenseForm({ ...licenseForm, expiry_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Renewal Date</label>
                  <input
                    type="date"
                    value={licenseForm.renewal_date}
                    onChange={(e) => setLicenseForm({ ...licenseForm, renewal_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Purchase Cost</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={licenseForm.purchase_cost}
                      onChange={(e) => setLicenseForm({ ...licenseForm, purchase_cost: e.target.value })}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                    <select
                      value={licenseForm.currency}
                      onChange={(e) => setLicenseForm({ ...licenseForm, currency: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="INR">INR</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Purchase Order #</label>
                  <input
                    type="text"
                    value={licenseForm.purchase_order_number}
                    onChange={(e) => setLicenseForm({ ...licenseForm, purchase_order_number: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vendor Email</label>
                  <input
                    type="email"
                    value={licenseForm.vendor_contact_email}
                    onChange={(e) => setLicenseForm({ ...licenseForm, vendor_contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vendor Phone</label>
                  <input
                    type="text"
                    value={licenseForm.vendor_contact_phone}
                    onChange={(e) => setLicenseForm({ ...licenseForm, vendor_contact_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">License Key</label>
                  <input
                    type="text"
                    value={licenseForm.license_key}
                    onChange={(e) => setLicenseForm({ ...licenseForm, license_key: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional: Store license key securely"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    value={licenseForm.notes}
                    onChange={(e) => setLicenseForm({ ...licenseForm, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Additional notes about this license..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                  <select
                    value={licenseForm.status}
                    onChange={(e) => setLicenseForm({ ...licenseForm, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Renewal Reminder (days)</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={licenseForm.renewal_reminder_days}
                    onChange={(e) => setLicenseForm({ ...licenseForm, renewal_reminder_days: parseInt(e.target.value) || 30 })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={licenseForm.auto_renew}
                      onChange={(e) => setLicenseForm({ ...licenseForm, auto_renew: e.target.checked })}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Auto-renew license</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowLicenseModal(false);
                  setSelectedLicense(null);
                  resetLicenseForm();
                }}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={selectedLicense ? handleUpdateLicense : handleCreateLicense}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={!licenseForm.software_name.trim()}
              >
                {selectedLicense ? 'Update License' : 'Create License'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign License Modal */}
      {showAssignModal && selectedLicense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">Assign License: {selectedLicense.software_name}</h2>
              <p className="text-sm text-slate-500 mt-1">{selectedLicense.available_licenses} licenses available</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Device</label>
                <DevicePicker
                  devices={devices}
                  value={assignmentForm.device_id}
                  onChange={(id) => setAssignmentForm({ ...assignmentForm, device_id: id })}
                  placeholder="Search devices (optional)…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">User ID</label>
                <input
                  type="text"
                  value={assignmentForm.user_id}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="User identifier (email, username, etc.)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">User Name</label>
                <input
                  type="text"
                  value={assignmentForm.user_name}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, user_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">User Email</label>
                <input
                  type="email"
                  value={assignmentForm.user_email}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, user_email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={assignmentForm.notes}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional notes about this assignment..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  resetAssignmentForm();
                }}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignLicense}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                disabled={!assignmentForm.device_id && !assignmentForm.user_id}
              >
                Assign License
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Renew License Modal */}
      {showRenewalModal && selectedLicense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">Renew License: {selectedLicense.software_name}</h2>
              <p className="text-sm text-slate-500 mt-1">Current expiry: {formatDate(selectedLicense.expiry_date)}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Renewal Date *</label>
                <input
                  type="date"
                  value={renewalForm.renewal_date}
                  onChange={(e) => setRenewalForm({ ...renewalForm, renewal_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">New Expiry Date *</label>
                <input
                  type="date"
                  value={renewalForm.expiry_date}
                  onChange={(e) => setRenewalForm({ ...renewalForm, expiry_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Renewal Type</label>
                  <select
                    value={renewalForm.renewal_type}
                    onChange={(e) => setRenewalForm({ ...renewalForm, renewal_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="annual">Annual</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="biannual">Bi-annual</option>
                    <option value="one-time">One-time</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Renewal Cost</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={renewalForm.renewal_cost}
                      onChange={(e) => setRenewalForm({ ...renewalForm, renewal_cost: e.target.value })}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                    <select
                      value={renewalForm.currency}
                      onChange={(e) => setRenewalForm({ ...renewalForm, currency: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="INR">INR</option>
                    </select>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Purchase Order #</label>
                <input
                  type="text"
                  value={renewalForm.purchase_order_number}
                  onChange={(e) => setRenewalForm({ ...renewalForm, purchase_order_number: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={renewalForm.notes}
                  onChange={(e) => setRenewalForm({ ...renewalForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional notes about this renewal..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRenewalModal(false);
                  resetRenewalForm();
                }}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRenewLicense}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                disabled={!renewalForm.renewal_date || !renewalForm.expiry_date}
              >
                Record Renewal
              </button>
            </div>
          </div>
        </div>
      )}
    </HubPage>
  );
};

export default LicenseManagement;
